import { PTAC_SYSTEM_PREFERENCE } from '@alpinehomeair/common';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import ProgressIndicator from 'components/common/progress-indicator';
import ProjectSettingButtonSelector from 'components/common/project-setting-button-selector';
import TextKey from 'components/common/text-key';
import Button from 'components/inputs/button';
import StepProgressBar from 'components/layout/step-progress-bar';

import AddressInfo from 'components/project-page/address-info';
import DuctedPreferencesForm from 'components/project-page/ducted-preferences-form';
import ExistingEquipment from 'components/project-page/existing-equipment';
import Map from 'components/project-page/google-map';
import HasExistingEquipmentStep from 'components/project-page/has-existing-equipment-step';
import PropertySnapshot from 'components/project-page/property-snapshot';
import StructureSubtypeSelector from 'components/project-page/structure-subtype-selector';
import StructureTypeSelector from 'components/project-page/structure-type-selector';
import ZipCodeInfo from 'components/project-page/zip-code-info';
import { useFooterConfig } from 'contexts/footer-context';
import {
  CustomerStructureType,
  ProjectSettingCodeType,
  SidebarSectionType,
  SystemSelectorInstanceType
} from 'data/types';
import {
  createLevelsFromProjectSettings,
  setShouldAutoGenerateLevels
} from 'features/ai';
import { setGoalStatement, updateProjectSetting } from 'features/project';

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { setActiveProjectStepId, setActiveTab } from 'features/ui';
import useEquipmentClearingSwitchGuard from 'hooks/use-equipment-clearing-switch-guard';
import { useStepTipSequence } from 'hooks/use-step-tip-sequence';
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { loadInstance, loadStructure } from 'utils/data';
import {
  asksHasExistingEquipment,
  equipmentPreferencesPageVisible,
  existingEquipmentPageVisible,
  hasExistingEquipmentAnswered,
  projectContinueTab
} from 'utils/existing-equipment-page-visibility';
import { isEmpty, isNotEmpty } from 'utils/helpers';
import {
  getProjectSetting,
  getProjectSettingValue,
  HYBRID_PATHWAY_SYSTEM_PREFERENCE,
  isHybridOnlyPreference
} from 'utils/project';
import { getProjectStepTips } from 'utils/project-step-tips';
import { projectProgressSteps, ProjectStep } from 'utils/project-steps';
import { errorModal, notification } from 'utils/ui';

const STEP_TITLES: Record<ProjectStep, string> = {
  get_started: 'Get Started',
  location: 'What is your address?',
  primary_or_detached: 'What are we working on?',
  project_type: 'What type of system do you want?',
  has_existing_equipment: 'Do you have existing equipment?'
};

const STEP_SUBTITLES: Record<ProjectStep, string> = {
  get_started: '',
  location:
    'We use your address to pull in key property details and automatically prefill information for your layout.',
  primary_or_detached:
    "Knowing what you're working on lets us apply the right sizing assumptions for a whole home versus a detached space like a garage or ADU.",
  project_type:
    'Your system preference helps us narrow the equipment options to match how you want to heat and cool the space.',
  has_existing_equipment:
    "If you have HVAC equipment today, we'll use it to recommend compatible replacements."
};

const STEP_PROGRESS_LABELS: Record<ProjectStep, string> = {
  get_started: 'Start',
  location: 'Location',
  primary_or_detached: 'Project Type',
  project_type: 'System Type',
  has_existing_equipment: 'Existing System'
};

export default function ProjectPage() {
  const dispatch = useAppDispatch();
  const { structureTypes, textKeys } = useAppSelector((state) => state.data);
  const {
    existingSystems,
    structure,
    structureUniqueId,
    structureSubtypeId,
    structureTypeId
  } = useAppSelector((state) => state.structure);
  const {
    attomData,
    locationData,
    liveChatData,
    goalStatement,
    projectSettings,
    instanceUniqueId,
    projectDetails
  } = useAppSelector((state) => state.project);
  const { shouldAutoGenerateLevels } = useAppSelector((state) => state.ai);
  const { activeProjectStepId } = useAppSelector((state) => state.ui);
  const { notes } = useAppSelector((state) => state.structure);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);
  const [structureTypeLoading, setStructureTypeLoading] = useState(false);
  const equipmentClearingSwitchGuard = useEquipmentClearingSwitchGuard();

  useEffect(() => {
    let timeout;
    if (
      attomData !== null &&
      structureTypeId === null &&
      structureSubtypeId === null
    ) {
      setStructureTypeLoading(true);
      timeout = setTimeout(() => setStructureTypeLoading(false), 5000);
    }
    return () => clearTimeout(timeout);
  }, [attomData]);

  useEffect(() => {
    setStructureTypeLoading(false);
  }, [structureTypeId, structureSubtypeId]);

  const refreshData = async () => {
    setRefreshingData(true);

    const newStructure = (
      await axios.get(`structure?uniqueId=${structureUniqueId}`)
    ).data as CustomerStructureType;
    const newInstance = (
      await axios.get(`instance?uniqueId=${instanceUniqueId}`)
    ).data as SystemSelectorInstanceType;

    if (newStructure && newInstance) {
      loadStructure(newStructure);
      await loadInstance(newInstance);
      notification('Data refreshed', 'success');
    } else {
      errorModal('Failed to refresh data');
    }

    setRefreshingData(false);
  };

  const generateGoalStatement = async () => {
    if (notes && !attomData && !goalStatement) {
      dispatch(setGoalStatement('Loading...'));
      const generateGoalStatementQuery = await axios.post(
        'ai/generate-goal-statement',
        { consultantNotes: notes }
      );
      dispatch(
        setGoalStatement(
          generateGoalStatementQuery.data.goalStatement ??
            "I'd like to ask you some questions about your project and then I can get a quote or order sent out to you. Can I start by getting the address of the property we're going to be building the system for?"
        )
      );
    }
  };

  const updateSettings = (data: { [key in ProjectSettingCodeType]?: any }) => {
    if (projectSettings.length === 0) {
      return;
    }

    Object.keys(data).forEach((key) => {
      if (data[key] !== null) {
        const projectSetting = getProjectSetting(key as ProjectSettingCodeType);

        if (projectSetting && isEmpty(projectSetting.value)) {
          dispatch(
            updateProjectSetting({
              attributeCode: key as ProjectSettingCodeType,
              value: data[key]
            })
          );
        }
      }
    });
  };

  const extractKeyLiveChatData = async () => {
    if (liveChatData) {
      const extractLiveChatData = await axios.post(
        'ai/extract-live-chat-data',
        {
          transcript: JSON.stringify(
            liveChatData.transcript.map(({ author, chatText }) => ({
              author,
              chatText
            }))
          )
        }
      );
      if (extractLiveChatData.data) {
        updateSettings(extractLiveChatData.data);
      } else {
        errorModal('Failed to extract project data from live chat');
      }
    }
  };

  useEffect(() => {
    if (projectSettings.length > 0) {
      setDataLoaded(true);
    }
  }, [projectSettings]);

  useEffect(() => {
    if (dataLoaded) {
      extractKeyLiveChatData();
    }
  }, [dataLoaded]);

  useEffect(() => {
    generateGoalStatement();
  }, [notes, attomData, goalStatement]);

  const handleContinue = useCallback(() => {
    if (
      shouldAutoGenerateLevels &&
      // On a committed Hybrid preference the deterministic auto-builder
      // (use-hybrid-structure-autobuild) owns level generation — the legacy
      // AI generator must not race it (AHA-6784).
      !isHybridOnlyPreference() &&
      structure.length === 0 &&
      structureUniqueId &&
      projectDetails?.projectId &&
      instanceUniqueId
    ) {
      dispatch(
        createLevelsFromProjectSettings({
          structureUniqueId,
          projectId: projectDetails.projectId,
          instanceUniqueId
        })
      );
    }
    dispatch(setShouldAutoGenerateLevels(false));
    const systemPreference = getProjectSettingValue('system_preference');
    dispatch(
      setActiveTab(
        projectContinueTab(
          existingEquipmentPageVisible(
            systemPreference,
            getProjectSettingValue('project_type')
          ),
          equipmentPreferencesPageVisible(systemPreference)
        )
      )
    );
  }, [
    dispatch,
    shouldAutoGenerateLevels,
    structure.length,
    structureUniqueId,
    projectDetails,
    instanceUniqueId
  ]);

  const locationComplete =
    isNotEmpty(attomData) ||
    (isNotEmpty(locationData?.zipcode) &&
      isNotEmpty(getProjectSettingValue('year_built')));

  const primaryOrDetachedComplete = isNotEmpty(
    getProjectSettingValue('primary_or_detached_structure')
  );
  const structureTypeComplete =
    isNotEmpty(structureTypeId) && isNotEmpty(structureSubtypeId);

  const structureComplete = primaryOrDetachedComplete && structureTypeComplete;

  const projectTypeComplete = isNotEmpty(
    getProjectSettingValue('system_preference')
  );
  const showHasExistingEquipmentStep = asksHasExistingEquipment(
    getProjectSettingValue('system_preference')
  );
  const hasExistingEquipmentComplete = hasExistingEquipmentAnswered(
    getProjectSettingValue('project_type')
  );

  const showExistingEquipment = ['Ducted Hybrid', 'Self Contained'].includes(
    getProjectSettingValue('system_preference')
  );

  const existingEquipmentComplete =
    (isNotEmpty(getProjectSettingValue('project_type')) &&
      getProjectSettingValue('project_type') === 'Designing New System') ||
    (existingSystems.length > 0 &&
      existingSystems.every((system) => system.components.length > 0));

  const showDuctedPreferences =
    showExistingEquipment &&
    getProjectSettingValue('project_type') !== 'Designing New System' &&
    getProjectSettingValue('project_type') === 'Replacing Old System';

  const ductedPreferencesComplete = isNotEmpty(
    getProjectSettingValue('replacing_with_different_equipment')
  );

  const projectTypeStepComplete =
    projectTypeComplete &&
    (!showExistingEquipment || existingEquipmentComplete) &&
    (!showDuctedPreferences || ductedPreferencesComplete);

  const canContinue =
    locationComplete &&
    structureComplete &&
    projectTypeStepComplete &&
    (!showHasExistingEquipmentStep || hasExistingEquipmentComplete);

  const stepRequirementsMet: Record<ProjectStep, boolean> = useMemo(
    () => ({
      get_started: true,
      location: locationComplete,
      primary_or_detached: primaryOrDetachedComplete && structureTypeComplete,
      project_type: projectTypeStepComplete,
      has_existing_equipment: hasExistingEquipmentComplete
    }),
    [
      locationComplete,
      primaryOrDetachedComplete,
      structureTypeComplete,
      projectTypeStepComplete,
      hasExistingEquipmentComplete
    ]
  );

  const visibleSteps: ProjectStep[] = useMemo(
    () => [
      'get_started',
      ...projectProgressSteps(showHasExistingEquipmentStep)
    ],
    [showHasExistingEquipmentStep]
  );

  const currentStep = visibleSteps.includes(activeProjectStepId as ProjectStep)
    ? (activeProjectStepId as ProjectStep)
    : visibleSteps[0];
  const setCurrentStep = useCallback(
    (step: ProjectStep) => {
      dispatch(setActiveProjectStepId(step));
    },
    [dispatch]
  );

  const currentStepIndex = visibleSteps.indexOf(currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;
  const stepDisplayNumber = currentStepIndex;

  useEffect(() => {
    if (!visibleSteps.includes(currentStep)) {
      setCurrentStep(
        currentStep === 'has_existing_equipment'
          ? 'project_type'
          : visibleSteps[0]
      );
    }
  }, [visibleSteps, currentStep, setCurrentStep]);

  const currentStepRequirementsMet = stepRequirementsMet[currentStep];

  const stepTips = useMemo(
    () =>
      getProjectStepTips({
        textKeys,
        hasAttomData: isNotEmpty(attomData),
        showExistingEquipment
      }),
    [textKeys, attomData, showExistingEquipment]
  );

  const projectSections: SidebarSectionType[] = useMemo(
    () =>
      projectProgressSteps(showHasExistingEquipmentStep).map((stepId) => ({
        id: stepId,
        title: STEP_TITLES[stepId],
        description: '',
        requirementsMet: stepRequirementsMet[stepId],
        required: true,
        progressBarLabel: STEP_PROGRESS_LABELS[stepId],
        steps: [
          {
            id: stepId,
            title: STEP_TITLES[stepId],
            description: '',
            status: stepRequirementsMet[stepId]
              ? ('complete' as const)
              : ('incomplete' as const),
            tips: stepTips[stepId],
            showTipsWhenComplete: stepId === 'location'
          }
        ]
      })),
    [stepRequirementsMet, stepTips, showHasExistingEquipmentStep]
  );

  const { currentStepTipSequence, reopenTipSequence } = useStepTipSequence({
    sections: projectSections,
    currentSectionId: currentStep,
    currentStepComplete: currentStepRequirementsMet
  });

  const handleProgressBarClick = useCallback(
    (sectionId: string) => {
      setCurrentStep(sectionId as ProjectStep);
    },
    [setCurrentStep]
  );

  const handleNext = useCallback(() => {
    if (isLastStep) {
      handleContinue();
    } else {
      setCurrentStep(visibleSteps[currentStepIndex + 1]);
    }
  }, [
    isLastStep,
    currentStepIndex,
    handleContinue,
    visibleSteps,
    setCurrentStep
  ]);

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(visibleSteps[currentStepIndex - 1]);
    }
  }, [isFirstStep, currentStepIndex, visibleSteps, setCurrentStep]);

  const nextDisabled = isLastStep ? !canContinue : !currentStepRequirementsMet;

  let nextDisabledReason: string | undefined;
  if (nextDisabled && isLastStep) {
    if (!attomData && !locationComplete) {
      nextDisabledReason =
        'Please enter a zip code and year built, then click Save to continue.';
    } else if (!stepRequirementsMet.primary_or_detached) {
      nextDisabledReason = 'Please complete the Structure Type step.';
    } else if (!projectTypeStepComplete) {
      nextDisabledReason = 'Please complete the System Type step.';
    } else if (showHasExistingEquipmentStep && !hasExistingEquipmentComplete) {
      nextDisabledReason = 'Please tell us if you have existing equipment.';
    }
  }

  useFooterConfig(
    useMemo(
      () =>
        currentStep === 'get_started'
          ? {}
          : {
              onBack: isFirstStep ? undefined : handleBack,
              onNext: handleNext,
              nextText: isLastStep ? 'Next Section' : 'Next',
              nextDisabled,
              nextDisabledReason,
              nextVariant: isLastStep ? ('green' as const) : ('blue' as const)
            },
      [
        currentStep,
        isFirstStep,
        handleBack,
        handleNext,
        isLastStep,
        nextDisabled,
        nextDisabledReason
      ]
    )
  );

  return (
    <div className='mx-auto flex h-full max-w-[1700px] flex-col gap-5'>
      <div className='flex min-h-0 grow justify-center overflow-y-auto py-5'>
        <div className='my-auto w-full max-w-[640px] px-6'>
          <div className='flex flex-col gap-5'>
            {currentStep !== 'get_started' && (
              <div className='flex flex-col gap-2'>
                <div className='flex items-center justify-center gap-2'>
                  <h2 className='text-center text-4xl font-semibold text-gray-900'>
                    Step {stepDisplayNumber}:{' '}
                    {currentStep === 'has_existing_equipment' ? (
                      <TextKey
                        code='step_existing_equipment_question_title'
                        fallback={STEP_TITLES.has_existing_equipment}
                      />
                    ) : (
                      STEP_TITLES[currentStep]
                    )}
                  </h2>
                  {currentStepTipSequence && (
                    <button
                      type='button'
                      className='relative flex items-center text-gray-400 hover:text-blue-500'
                      onClick={reopenTipSequence}
                      aria-label='Show step tip'
                    >
                      <QuestionMarkCircleIcon className='h-7 w-7' />
                    </button>
                  )}
                </div>
                <p className='text-center text-lg text-gray-500'>
                  {STEP_SUBTITLES[currentStep]}
                </p>
              </div>
            )}
            {currentStep !== 'get_started' && (
              <StepProgressBar
                sections={projectSections}
                currentSectionId={currentStep}
                onSectionClick={handleProgressBarClick}
              />
            )}
            <div>
              <section
                className={
                  currentStep === 'get_started'
                    ? ''
                    : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm'
                }
              >
                {currentStep === 'get_started' && (
                  <div className='flex flex-col items-center gap-5 py-6 text-center'>
                    <div className='text-4xl font-semibold text-gray-800'>
                      <TextKey
                        code='project_page_title'
                        fallback='Get the Right HVAC System — Designed for Your Home'
                      />
                    </div>
                    <div className='text-lg text-gray-500'>
                      <TextKey
                        code='project_page_subtitle'
                        fallback='We handle the hard parts. In a few minutes, we size your home, match the right equipment, and build a complete system—no HVAC experience needed.'
                      />
                    </div>
                    <Button
                      text='Get Started'
                      color='primary'
                      size='lg'
                      onClick={handleNext}
                    />
                  </div>
                )}
                {currentStep === 'location' && (
                  <div className='flex flex-col gap-4'>
                    {!attomData && (
                      <div className='flex flex-col gap-4'>
                        <div id='project-address-info'>
                          <AddressInfo />
                        </div>
                        <div className='flex items-center gap-3 pt-2'>
                          <div className='h-px flex-1 bg-gray-300' />
                          <span className='text-sm font-medium text-gray-500'>
                            OR
                          </span>
                          <div className='h-px flex-1 bg-gray-300' />
                        </div>
                        <div id='project-zip-info'>
                          <div className='mb-2 flex items-center gap-2'>
                            <span className='block text-sm font-medium text-gray-700'>
                              Zip Code & Year Built
                            </span>
                          </div>
                          <ZipCodeInfo />
                        </div>
                      </div>
                    )}
                    {attomData && (
                      <div className='flex flex-col gap-4'>
                        <div id='project-address-info'>
                          <AddressInfo />
                        </div>
                        <Map size='small' />
                        <PropertySnapshot attomData={attomData} />
                      </div>
                    )}
                  </div>
                )}
                {currentStep === 'primary_or_detached' && (
                  <div className='flex flex-col gap-4'>
                    <div
                      id='project-primary-detached'
                      className='flex flex-col'
                    >
                      <span className='mb-2 block text-sm font-medium text-gray-700'>
                        Is this your main home or a separate building (like a
                        garage, guest house, or addition)?
                      </span>
                      <ProjectSettingButtonSelector
                        code='primary_or_detached_structure'
                        options={[
                          {
                            label: 'Primary Structure',
                            value: 'Primary Structure'
                          },
                          {
                            label: 'Detached Structure',
                            value: 'Detached Structure'
                          }
                        ]}
                      />
                    </div>
                    <div
                      id='project-structure-type'
                      className='grid grid-cols-2 gap-4'
                    >
                      <div className='flex flex-col gap-2'>
                        <div className='flex items-center gap-2'>
                          <ProgressIndicator
                            required
                            checked={structureTypeId !== null}
                          />
                          <span className='font-semibold text-gray-800'>
                            Structure Type
                          </span>
                        </div>
                        <StructureTypeSelector loading={structureTypeLoading} />
                      </div>
                      <div className='flex flex-col gap-2'>
                        <div className='flex items-center gap-2'>
                          <ProgressIndicator
                            required
                            checked={structureSubtypeId !== null}
                          />
                          <span className='font-semibold text-gray-800'>
                            {structureTypeId !== null
                              ? `${
                                  structureTypes.find(
                                    (s) => s.id === structureTypeId
                                  )?.label || 'Unknown'
                                } Type`
                              : 'Structure Subtype'}
                          </span>
                        </div>
                        <StructureSubtypeSelector
                          loading={structureTypeLoading}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {currentStep === 'project_type' && (
                  <div className='flex flex-col gap-6'>
                    <div>
                      <div
                        id='project-system-preference'
                        className='flex flex-col'
                      >
                        <span className='mb-2 block text-sm font-medium text-gray-700'>
                          Choose the kind of system you&apos;re interested in
                          (we&apos;ll guide you if you&apos;re unsure).
                        </span>
                        <ProjectSettingButtonSelector
                          code='system_preference'
                          // Ducted/Hybrid→EP-only (ductless) with entered
                          // equipment asks for confirmation and wipes on
                          // confirm (AHA-6681 pathway exclusivity). Hybrid
                          // is not EP-only — Ducted→Hybrid keeps equipment.
                          beforeChange={equipmentClearingSwitchGuard}
                          options={[
                            {
                              label: 'Ductless Mini Split',
                              value: 'Ductless Mini Split'
                            },
                            {
                              label: 'Ducted',
                              // AHA-6681: kept enabled unconditionally (branch
                              // change for testing existing equipment); develop
                              // gates it behind developerMode.
                              value: 'Ducted'
                            },
                            {
                              label: 'Hybrid Ductless/Ducted',
                              value: HYBRID_PATHWAY_SYSTEM_PREFERENCE
                            },
                            {
                              label: 'Hotel Style (PTAC)',
                              value: PTAC_SYSTEM_PREFERENCE
                            }
                          ]}
                          columns={2}
                        />
                      </div>
                    </div>
                    {showExistingEquipment && (
                      <div id='project-existing-equipment'>
                        <div className='mb-4 flex items-center gap-3'>
                          <ProgressIndicator
                            checked={existingEquipmentComplete}
                          />
                          <h2 className='text-lg font-semibold text-gray-900'>
                            Existing Equipment
                          </h2>
                        </div>
                        <div className='flex flex-col items-center gap-4'>
                          <span className='text-sm font-medium text-gray-700'>
                            Are we replacing existing HVAC equipment?
                          </span>
                          <ProjectSettingButtonSelector
                            code='project_type'
                            options={[
                              {
                                label: 'Yes',
                                value: 'Replacing Old System'
                              },
                              {
                                label: 'No',
                                value: 'Designing New System'
                              }
                            ]}
                          />
                          {getProjectSettingValue('project_type') ===
                            'Replacing Old System' && (
                            <div className='w-full'>
                              <ExistingEquipment />
                              <div className='mt-4 flex justify-center'>
                                <Button
                                  text='Refresh Data'
                                  onClick={refreshData}
                                  disabled={refreshingData}
                                  loading={refreshingData}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {showDuctedPreferences && (
                      <div>
                        <div className='mb-4 flex items-center gap-3'>
                          <ProgressIndicator
                            checked={ductedPreferencesComplete}
                          />
                          <h2 className='text-lg font-semibold text-gray-900'>
                            Preferences
                          </h2>
                        </div>
                        <DuctedPreferencesForm />
                      </div>
                    )}
                  </div>
                )}
                {currentStep === 'has_existing_equipment' && (
                  <HasExistingEquipmentStep />
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
